import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { useHasPrivilegeStatus } from "../../../hooks/useHasPrivilege";
import { IMPLEMENTATION_RECORD_PRIVILEGES } from "../../../services/permissionsService";
import {
  fetchAllImplementationRecordsForPack,
  fetchAssessmentPackDetail,
  fetchImplementationRecordGroups,
  type AssessmentPack,
} from "../../../services/implementationRecordService";
import { TruncatedTooltipCell } from "../../AiPlanPage/TruncatedTooltipCell";
import {
  AiHubDetailErrorState,
  AiHubDetailLoadingState,
} from "../AiHubDetailPageState";
import { AI_HUB_PATH, readAssessmentPackQuery } from "../constants";
import { resultLoadErrorMessage } from "../resultLoadError";
import AssessmentPackBanner from "./AssessmentPackBanner";
import ImplementationRecordDetailContent, {
  ImpactStatusChips,
} from "./ImplementationRecordDetailContent";
import {
  assessorInitials,
  buildPackTree,
  formatObjectItemLines,
  type PackTreeChild,
  type PackTreeGroup,
} from "./implementationRecordView";

const AVATAR_COLORS = ["#2563EB", "#0F766E", "#C2410C", "#7C3AED", "#B45309"];

function avatarColor(name: string): string {
  let hash = 0;
  for (const ch of name) hash = (hash + ch.charCodeAt(0)) % AVATAR_COLORS.length;
  return AVATAR_COLORS[hash] ?? "#2563EB";
}

export default function AssessmentPackDetailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const query = readAssessmentPackQuery(searchParams);
  const { has, loaded } = useHasPrivilegeStatus(
    IMPLEMENTATION_RECORD_PRIVILEGES.read,
  );

  const [pack, setPack] = useState<AssessmentPack | null>(null);
  const [groups, setGroups] = useState<PackTreeGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedGroupName, setSelectedGroupName] = useState("");
  const [selectedChildKey, setSelectedChildKey] = useState("");
  const [detailOpen, setDetailOpen] = useState(false);
  const [activeRecordId, setActiveRecordId] = useState("");

  const ready = Boolean(query.referenceObjectType && query.referenceObjectId);

  const goHome = () => navigate(AI_HUB_PATH);

  useEffect(() => {
    if (!loaded || !has || !ready) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    const packQuery = {
      referenceObjectType: query.referenceObjectType,
      referenceObjectId: query.referenceObjectId,
      referenceObjectVersion: query.referenceObjectVersion || undefined,
    };

    Promise.all([
      fetchAssessmentPackDetail(packQuery),
      fetchImplementationRecordGroups(packQuery),
      fetchAllImplementationRecordsForPack(packQuery),
    ])
      .then(([nextPack, nextGroups, records]) => {
        if (cancelled) return;
        const tree = buildPackTree(nextGroups, records);
        setPack(nextPack);
        setGroups(tree);
        const firstGroup = tree[0];
        const firstChild = firstGroup?.children[0];
        setSelectedGroupName(firstGroup?.assessmentName ?? "");
        setSelectedChildKey(firstChild?.key ?? "");
        setDetailOpen(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setPack(null);
        setGroups([]);
        setError(
          resultLoadErrorMessage(err, "Failed to load this Assessment Pack."),
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    has,
    loaded,
    query.referenceObjectId,
    query.referenceObjectType,
    query.referenceObjectVersion,
    ready,
  ]);

  useEffect(() => {
    if (groups.length === 0) {
      setSelectedGroupName("");
      setSelectedChildKey("");
      return;
    }
    const groupStillVisible = groups.some(
      (group) => group.assessmentName === selectedGroupName,
    );
    if (!groupStillVisible) {
      const first = groups[0];
      setSelectedGroupName(first.assessmentName);
      setSelectedChildKey(first.children[0]?.key ?? "");
      setDetailOpen(false);
      return;
    }
    const group =
      groups.find((item) => item.assessmentName === selectedGroupName) ??
      null;
    if (
      group &&
      selectedChildKey &&
      !group.children.some((child) => child.key === selectedChildKey)
    ) {
      setSelectedChildKey(group.children[0]?.key ?? "");
      setDetailOpen(false);
    }
  }, [selectedChildKey, selectedGroupName, groups]);

  const selectedGroup = useMemo(
    () =>
      groups.find((group) => group.assessmentName === selectedGroupName) ??
      null,
    [selectedGroupName, groups],
  );
  const selectedChild: PackTreeChild | null = useMemo(() => {
    if (!selectedGroup) return null;
    return (
      selectedGroup.children.find((child) => child.key === selectedChildKey) ??
      null
    );
  }, [selectedChildKey, selectedGroup]);

  const detailRecords = selectedChild?.records ?? [];
  const activeRecord =
    detailRecords.find((record) => record.id === activeRecordId) ??
    detailRecords[0] ??
    null;

  if (!loaded || (has && ready && loading)) {
    return <AiHubDetailLoadingState />;
  }

  if (!has) {
    return (
      <AiHubDetailErrorState
        resultId="Assessment Pack"
        message="You do not have permission to view this Assessment Pack."
        onBack={goHome}
      />
    );
  }

  if (!ready) {
    return (
      <AiHubDetailErrorState
        resultId="Assessment Pack"
        message="reference_object_type and reference_object_id are required."
        onBack={goHome}
      />
    );
  }

  if (error) {
    return (
      <AiHubDetailErrorState
        resultId={query.referenceObjectId}
        message={error}
        onBack={goHome}
      />
    );
  }

  const title =
    pack?.name ||
    `${query.referenceObjectId}${query.referenceObjectVersion ? ` ${query.referenceObjectVersion}` : ""}`;
  const selectedTitle = selectedChild
    ? formatObjectItemLines(selectedChild.object).title
    : selectedGroup?.assessmentName || "Assessments";

  return (
    <Box
      sx={{
        height: "100vh",
        overflow: "hidden",
        boxSizing: "border-box",
        bgcolor: "#f7f8fa",
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <AssessmentPackBanner
        title={title}
        impactedObjectTypes={pack?.impactedObjectTypes ?? []}
      />

      {detailOpen && activeRecord ? (
        <Box
          sx={{
            bgcolor: "#fff",
            borderRadius: "8px",
            p: "22px 24px",
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 600, flex: 1 }}>
              {selectedTitle}
            </Typography>
            <IconButton
              aria-label="Close"
              onClick={() => setDetailOpen(false)}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Tabs
            value={activeRecord.id}
            onChange={(_event, value: string) => setActiveRecordId(value)}
            sx={{ mt: 1, minHeight: 36 }}
          >
            {detailRecords.map((record) => (
              <Tab
                key={record.id}
                value={record.id}
                label={record.assessorName || record.assessor || record.id}
                sx={{ textTransform: "none", minHeight: 36 }}
              />
            ))}
          </Tabs>
          <Box sx={{ mt: 2.5 }}>
            <ImplementationRecordDetailContent record={activeRecord} />
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "stretch",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              flex: "none",
              width: 260,
              bgcolor: "#fff",
              borderRadius: "8px",
              p: "18px 12px",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 1, px: 0.5, flex: "none" }}>
              Assessments
            </Typography>
            <Box
              component="nav"
              aria-label="Assessment menu"
              sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}
            >
            {groups.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: "#8C8C8C", px: 0.5, py: 2 }}>
                No matching assessments.
              </Typography>
            ) : (
              groups.map((group) => {
                const groupActive = selectedGroupName === group.assessmentName;
                return (
                  <Box key={group.assessmentName} sx={{ mb: 0.5 }}>
                    <Box
                      component="button"
                      type="button"
                      onClick={() => {
                        setSelectedGroupName(group.assessmentName);
                        setSelectedChildKey(group.children[0]?.key ?? "");
                        setDetailOpen(false);
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: 236,
                        height: 36,
                        boxSizing: "border-box",
                        border: 0,
                        bgcolor: groupActive
                          ? "rgba(231, 236, 252, 0.5)"
                          : "transparent",
                        cursor: "pointer",
                        textAlign: "left",
                        pt: "8px",
                        pr: "12px",
                        pb: "8px",
                        pl: "12px",
                        borderRadius: "4px",
                        opacity: 1,
                      }}
                    >
                      <TruncatedTooltipCell
                        text={group.assessmentName}
                        sx={{
                          fontSize: 13,
                          fontWeight: groupActive ? 600 : 500,
                          color: groupActive ? "#295EF1" : "#262626",
                          flex: 1,
                          pr: 1,
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: groupActive ? 600 : 400,
                          color: groupActive ? "#295EF1" : "#262626",
                          flex: "none",
                        }}
                      >
                        ({group.recordCount})
                      </Typography>
                    </Box>
                    {group.children.map((child) => {
                      const childActive =
                        groupActive && selectedChildKey === child.key;
                      const childColor = childActive ? "#295EF1" : "#262626";
                      return (
                        <Box
                          key={child.key}
                          component="button"
                          type="button"
                          onClick={() => {
                            setSelectedGroupName(group.assessmentName);
                            setSelectedChildKey(child.key);
                            setDetailOpen(false);
                          }}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            width: 236,
                            boxSizing: "border-box",
                            border: 0,
                            bgcolor: "transparent",
                            cursor: "pointer",
                            textAlign: "left",
                            py: 0.75,
                            pl: "24px",
                            pr: "12px",
                          }}
                        >
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              bgcolor: childColor,
                              flex: "none",
                            }}
                          />
                          <TruncatedTooltipCell
                            text={child.label}
                            sx={{
                              fontSize: 13,
                              color: childColor,
                              fontWeight: childActive ? 600 : 400,
                              flex: 1,
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                );
              })
            )}
            </Box>
          </Box>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              overflowY: "auto",
              bgcolor: "#fff",
              borderRadius: "8px",
              p: "22px 24px",
            }}
          >
            <Typography sx={{ fontSize: 18, fontWeight: 600, mb: 2 }}>
              {selectedTitle}
            </Typography>
            {selectedChild ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fit, minmax(min(100%, 280px), 1fr))",
                  gap: 2,
                }}
              >
                {selectedChild.records.map((record) => (
                  <Box
                    key={record.id}
                    component="button"
                    type="button"
                    onClick={() => {
                      setActiveRecordId(record.id);
                      setDetailOpen(true);
                    }}
                    sx={{
                      border: "1px solid #E5E7EB",
                      borderRadius: "8px",
                      bgcolor: "#fff",
                      p: 2,
                      textAlign: "left",
                      cursor: "pointer",
                      "&:hover": { borderColor: "#295EF1" },
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                    >
                      <Box
                        sx={{
                          width: 26,
                          height: 26,
                          borderRadius: "999px",
                          bgcolor: avatarColor(record.assessorName),
                          color: "#fff",
                          fontSize: 11,
                          fontWeight: 600,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {assessorInitials(record.assessorName || record.assessor)}
                      </Box>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, flex: 1 }}>
                        {record.assessorName || record.assessor}
                      </Typography>
                      <ImpactStatusChips
                        impacted={record.impacted}
                        impactedLevel={record.impactedLevel}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 13,
                        color: "#404040",
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {record.assessmentSummary || "No summary."}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ display: "flex", justifyContent: "center", pt: 8 }}>
                {loading ? (
                  <CircularProgress size={28} />
                ) : (
                  <Typography sx={{ fontSize: 13, color: "#8C8C8C" }}>
                    Select an assessment from the sidebar.
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}
